import { bool, string, shape } from 'prop-types';
import React, { Component, createRef } from 'react';
import classnames from 'classnames';
import AmpImg from 'components/AmpImg';
import Figure from 'components/Figure';
import withCompositeProduct from 'lib/withCompositeProduct';
import { getAffiliateLinkProps } from 'util/affiliates/amazon';
import { parse } from 'util/json';
import { MQ_MIDLARGE, MQ_LARGE, MQ_MEDIUM } from 'constants/mq';
import styles from './styles';

/**
 * This component is used for rendering product information for 'product' and
 * 'productTeaser' embeds. The differences in the data are normalized by the
 * withCompositeProduct adapter.
 */
export class Product extends Component {
  constructor(props) {
    super(props);
    this.state = {
      price: props.price,
      retailerName: props.retailerName,
      salePrice: props.salePrice
    };
    this.linkRef = createRef();
    this.observer = undefined;
  }

  componentDidMount() {
    const linkElement = this.linkRef && this.linkRef.current;
    this.observer = new MutationObserver(this.handleChangeLinkAttribute);
    this.observer.observe(linkElement, { attributes: true });
  }

  componentWillUnmount() {
    this.observer.disconnect();
  }

  // When Narrativ updates the retailer/price for a link, we need to update the UI display
  // to match the new values.
  handleChangeLinkAttribute = mutationList => {
    const linkElement = this.linkRef && this.linkRef.current;
    mutationList.some(mutation => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-bamx-auction'
      ) {
        const bamxData = parse(
          linkElement.getAttribute('data-bamx-auction'),
          {}
        );
        const { auction_result: auctionResult } = bamxData;
        const { product } = auctionResult || {};
        if (product) {
          const auctionPrice = `${product.price || ''}`;
          const formattedPrice = `${
            auctionPrice && auctionPrice.indexOf('$') !== 0 ? '$' : ''
          }${auctionPrice}`;
          this.setState(({ price, retailerName, salePrice }) => ({
            price: formattedPrice || price,
            retailerName:
              (product.merchant && product.merchant.name) || retailerName,
            salePrice: product.price ? undefined : salePrice
          }));
        }
        return true;
      }
      return false;
    });
  };

  render() {
    const { price, retailerName, salePrice } = this.state;
    const {
      description,
      imageUrl,
      isAffiliate,
      isAmp,
      name,
      url: originalUrl,
      variant
    } = this.props;

    const wrapperClasses = classnames('Product', {
      'Product--button': !(
        price ||
        description ||
        imageUrl ||
        name ||
        retailerName
      ),
      [`Product--${variant}`]: variant
    });

    const defaultLinkProps = {
      href: originalUrl
    };

    // handle tagging affiliate links
    const linkProps = isAffiliate
      ? getAffiliateLinkProps(originalUrl)
      : defaultLinkProps;

    return (
      // NOTE, typically noopener and noreferrer should be used when opening
      // links in a new tab. However it is unclear if this breaks certain affiliate
      // services, or violates Amazon's terms of use.
      <a
        className={wrapperClasses}
        ref={this.linkRef}
        rel="nofollow"
        target="_blank"
        {...linkProps}
      >
        {imageUrl && (
          <div className="Product__image">
            {isAmp ? (
              <AmpImg
                url={imageUrl}
                alt={`Product Image: ${name}`}
                originalWidth={1}
                originalHeight={1}
                maxSize={{
                  width: 534
                }}
              />
            ) : (
              <Figure
                alt={`Product Image: ${name}`}
                isPinnable={false}
                maxWidth={365}
                originalHeight={365}
                originalWidth={365} // force them all to be 1:1
                placement="product"
                sourceSizes={[
                  {
                    media: MQ_MIDLARGE,
                    layoutWidth: {
                      value: 365,
                      units: 'px'
                    }
                  },
                  {
                    media: MQ_LARGE,
                    layoutWidth: {
                      value: 305,
                      units: 'px'
                    }
                  },
                  {
                    media: MQ_MEDIUM,
                    layoutWidth: {
                      value: 348,
                      units: 'px'
                    }
                  },
                  {
                    layoutWidth: {
                      value: 100,
                      units: 'vw'
                    }
                  }
                ]}
                resizeMode="scaleFit"
                url={imageUrl}
              />
            )}
          </div>
        )}

        <div className="Product__body">
          {name && <div className="Product__name">{name}</div>}

          {price && (
            <div className="Product__price">
              {salePrice && (
                <span
                  className="Product__original-price"
                  data-test="originalPrice"
                >
                  {price}
                </span>
              )}
              <span className="Product__current-price" data-test="currentPrice">
                {salePrice || price}
              </span>
            </div>
          )}

          {retailerName && (
            <div className="Product__retailer" data-test="retailer">
              {retailerName}
            </div>
          )}

          {description && (
            <div
              className="Product__description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}

          <div className="Product__cta" aria-label="buy now">
            Buy Now
          </div>
        </div>
        <style jsx>{styles}</style>
      </a>
    );
  }
}

Product.propTypes = {
  description: string,
  image: shape({
    url: string
  }),
  imageUrl: string,
  isAffiliate: bool,
  isAmp: bool,
  name: string,
  price: string,
  retailerName: string,
  salePrice: string,
  url: string.isRequired,
  variant: string
};

Product.defaultProps = {
  description: undefined,
  image: undefined,
  imageUrl: undefined,
  isAffiliate: false,
  isAmp: false,
  name: undefined,
  price: undefined,
  retailerName: undefined,
  salePrice: undefined,
  variant: 'in-post'
};

export default withCompositeProduct(Product);
